import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { login, sendOtp, validateOtp, trustDevice, clearError } from '../../store/slices/authSlice';
import { fetchOperationsData } from '../../store/slices/operationsSlice';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import AuthLayout from '../../components/layout/AuthLayout';
import { useDeviceInfo } from '../../hooks/useDeviceInfo';
import { AuthState } from '../../types';

// ── Shared input style ────────────────────────────────────────────────────────
const inputClass =
  'w-full px-4 py-3.5 rounded-xl border text-slate-800 text-sm font-medium ' +
  'bg-slate-50 border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 ' +
  'focus:border-transparent placeholder:text-slate-400 transition';

const labelClass = 'block text-sm font-semibold text-slate-700 mb-2';

const BtnSpinner = () => (
  <svg className="animate-spin mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
  </svg>
);

const LoginPage: React.FC = () => {
  const [mobilePhone, setMobilePhone]     = useState('');
  const [password, setPassword]           = useState('');
  const [rememberMe, setRememberMe]       = useState(false);
  const [isDistributor, setIsDistributor] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [showPassword, setShowPassword]   = useState(false);

  const [otpCode, setOtpCode]                   = useState('');
  const [trustDeviceChecked, setTrustDeviceChecked] = useState(false);
  const [step, setStep] = useState<'login' | 'otp'>('login');

  const dispatch   = useAppDispatch();
  const navigate   = useNavigate();
  const deviceInfo = useDeviceInfo();

  const { isLoading, error, requireOtp, requireTrustDevice, currentDeviceId, otpVerified } =
    useAppSelector((state) => state.auth as AuthState);

  useEffect(() => { dispatch(clearError()); }, [dispatch]);

  useEffect(() => {
    if (requireOtp && currentDeviceId) setStep('otp');
  }, [requireOtp, currentDeviceId]);

  useEffect(() => {
    if (otpVerified) {
      if (requireTrustDevice && trustDeviceChecked && currentDeviceId) {
        dispatch(trustDevice({ deviceId: currentDeviceId, trustDevice: trustDeviceChecked ? '1' : '0' }));
      }
      dispatch(fetchOperationsData());
      navigate('/dashboard');
    }
  }, [otpVerified, requireTrustDevice, trustDeviceChecked, currentDeviceId, dispatch, navigate]);

  const validatePhone = (n: string) => /^[0-9]{10}$/.test(n);

  const handleLoginSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);
    if (!validatePhone(mobilePhone)) {
      setValidationError('El número debe tener 10 dígitos numéricos');
      return;
    }
    try {
      await dispatch(login({ mobile_phone: mobilePhone, password, is_distributor: isDistributor })).unwrap();
      const otpResult = await dispatch(sendOtp({
        deviceId: deviceInfo.deviceId || 'unknown-device',
        deviceName: deviceInfo.deviceName || 'PayPlus POS',
      })).unwrap();
      if (!otpResult.requireOtp) {
        dispatch(fetchOperationsData());
        navigate('/dashboard');
      }
    } catch (_) {}
  };

  const handleOtpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpCode || !currentDeviceId) return;
    try {
      await dispatch(validateOtp({ otp: otpCode, deviceId: currentDeviceId })).unwrap();
    } catch (_) {}
  };

  const displayError = validationError || error;

  return (
    <AuthLayout>
      {/* Title */}
      <div className="mb-7">
        <h2 className="text-2xl font-black text-slate-800">
          {step === 'login' ? 'Iniciar sesión' : 'Verificar identidad'}
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          {step === 'login'
            ? 'Ingresa tus credenciales para continuar'
            : 'Ingresa el código enviado a tu teléfono'}
        </p>
      </div>

      {/* Error banner */}
      {displayError && (
        <div className="mb-5 flex items-start gap-3 px-4 py-3 rounded-xl"
          style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
          </svg>
          <p className="text-sm font-medium text-red-600">{displayError}</p>
        </div>
      )}

      {step === 'login' ? (
        <form onSubmit={handleLoginSubmit} className="space-y-5">
          {/* Phone */}
          <div>
            <label htmlFor="mobile_phone" className={labelClass}>Número de Teléfono</label>
            <input
              id="mobile_phone" type="tel" autoComplete="tel" required
              value={mobilePhone}
              onChange={(e) => { setMobilePhone(e.target.value); setValidationError(null); }}
              className={inputClass}
              placeholder="0000-000-0000"
            />
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className={labelClass}>Contraseña</label>
            <div className="relative">
              <input
                id="password" type={showPassword ? 'text' : 'password'}
                autoComplete="current-password" required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass + ' pr-12'}
                placeholder="••••••••"
              />
              <button type="button" tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-1"
                onClick={() => setShowPassword(v => !v)}>
                {showPassword
                  ? <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/></svg>
                  : <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                }
              </button>
            </div>
          </div>

          {/* Options row */}
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} />
              <span className="text-sm text-slate-600">Recordarme</span>
            </label>
            <button type="button" className="text-sm font-semibold text-blue-600 hover:text-blue-800 transition"
              onClick={() => navigate('/auth/recuperar-contrasena')}>
              ¿Olvidó su contraseña?
            </button>
          </div>

          {/* Distributor */}
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              checked={isDistributor} onChange={(e) => setIsDistributor(e.target.checked)} />
            <span className="text-sm text-slate-600">Soy distribuidor</span>
          </label>

          {/* Submit */}
          <button type="submit"
            disabled={isLoading || !mobilePhone || !password || !validatePhone(mobilePhone)}
            className="w-full flex items-center justify-center py-3.5 rounded-xl text-white text-sm font-bold tracking-wide transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              boxShadow: '0 4px 16px rgba(37,99,235,0.4)',
            }}>
            {isLoading ? <><BtnSpinner />Iniciando sesión...</> : 'Iniciar Sesión →'}
          </button>
        </form>
      ) : (
        <form onSubmit={handleOtpSubmit} className="space-y-5">
          <div className="p-4 rounded-xl text-sm text-slate-600"
            style={{ background: '#f0f9ff', border: '1px solid #bae6fd' }}>
            Enviamos un código de 4 dígitos a tu teléfono registrado. Ingrésalo para verificar tu identidad.
          </div>

          <div>
            <label htmlFor="otp" className={labelClass}>Código de Verificación</label>
            <input id="otp" type="text" required maxLength={4}
              value={otpCode} onChange={(e) => setOtpCode(e.target.value)}
              className={inputClass + ' text-center text-3xl tracking-[1rem] font-black'}
              placeholder="- - - -"
            />
          </div>

          {requireTrustDevice && (
            <label className="flex items-start gap-3 cursor-pointer select-none p-3 rounded-xl"
              style={{ background: '#f0f9ff', border: '1px solid #bae6fd' }}>
              <input type="checkbox" className="mt-0.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                checked={trustDeviceChecked} onChange={(e) => setTrustDeviceChecked(e.target.checked)} />
              <span className="text-sm text-slate-600 leading-snug">
                Confiar en este dispositivo (no volver a pedir código aquí)
              </span>
            </label>
          )}

          <button type="submit" disabled={isLoading || !otpCode}
            className="w-full flex items-center justify-center py-3.5 rounded-xl text-white text-sm font-bold tracking-wide transition-all duration-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              boxShadow: '0 4px 16px rgba(37,99,235,0.4)',
            }}>
            {isLoading ? <><BtnSpinner />Verificando...</> : 'Verificar Código →'}
          </button>

          <button type="button" onClick={() => setStep('login')}
            className="w-full text-sm text-slate-500 hover:text-slate-700 transition py-2">
            ← Volver al inicio de sesión
          </button>
        </form>
      )}
    </AuthLayout>
  );
};

export default LoginPage;
